import { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { X, Minimize2, Maximize2 } from 'lucide-react';
import 'xterm/css/xterm.css';

interface TerminalPanelProps {
  isOpen: boolean;
  onClose: () => void;
  workingDirectory?: string;
}

export function TerminalPanel({ isOpen, onClose, workingDirectory }: TerminalPanelProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);

  // Initialize terminal
  useEffect(() => {
    if (!isOpen || !terminalRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontSize: 14,
      fontFamily: 'Consolas, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#ffffff',
        cursorAccent: '#000000',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#ffffff',
      },
      convertEol: true,
      scrollback: 1000,
    });

    const fit = new FitAddon();
    fitAddon.current = fit;
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());

    term.open(terminalRef.current!);
    fit.fit();

    // Welcome message
    term.writeln('\x1b[32mWelcome to AI Code Editor Terminal\x1b[0m');
    term.writeln('\x1b[90mThis is a simulated terminal for demonstration purposes\x1b[0m');
    term.writeln('\x1b[90mCommands are executed locally in the browser\x1b[0m');
    term.writeln('');

    // Command history
    let commandHistory: string[] = [];
    let historyIndex = -1;
    let currentInput = '';

    // Handle keyboard input
    term.onData((e) => {
      // Handle special keys
      if (e === '\r') {
        // Enter - execute command
        term.writeln('');
        commandHistory.push(currentInput);
        historyIndex = commandHistory.length;
        
        // Process command
        processCommand(term, currentInput);
        currentInput = '';
      } else if (e === '\x7f' || e === '\b') {
        // Backspace
        if (currentInput.length > 0) {
          currentInput = currentInput.slice(0, -1);
          term.write('\b \b');
        }
      } else if (e === '\x1b[A') {
        // Up arrow - history
        if (historyIndex > 0) {
          historyIndex--;
          const cmd = commandHistory[historyIndex];
          term.write('\x1b[2K\r'); // Clear line
          term.write(`$ ${cmd}`);
          currentInput = cmd;
        }
      } else if (e === '\x1b[B') {
        // Down arrow - history
        if (historyIndex < commandHistory.length - 1) {
          historyIndex++;
          const cmd = commandHistory[historyIndex];
          term.write('\x1b[2K\r'); // Clear line
          term.write(`$ ${cmd}`);
          currentInput = cmd;
        } else if (historyIndex === commandHistory.length - 1) {
          historyIndex = commandHistory.length;
          term.write('\x1b[2K\r'); // Clear line
          term.write('$ ');
          currentInput = '';
        }
      } else if (e === '\x1b[C') {
        // Right arrow - ignore
      } else if (e === '\x1b[D') {
        // Left arrow - ignore
      } else if (e === '\x03') {
        // Ctrl+C
        term.writeln('^C');
        currentInput = '';
      } else if (e === '\x0c') {
        // Ctrl+L - clear
        term.clear();
      } else if (e === '\t') {
        // Tab - basic autocomplete
        // Could be enhanced for actual autocomplete
      } else if (e.charCodeAt(0) >= 32) {
        // Regular character
        currentInput += e;
        term.write(e);
      }
    });

    // Show prompt
    const showPrompt = () => {
      term.write('\r\n\x1b[34muser@editor\x1b[0m:\x1b[33m~\x1b[0m$ ');
    };
    showPrompt();

    // Store prompt function for later use
    (term as any).showPrompt = showPrompt;

    // Resize handler
    const handleResize = () => {
      fit.fit();
    };
    window.addEventListener('resize', handleResize);

    terminalInstance.current = term;

    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
    };
  }, [isOpen]);

  // Process command (simulated)
  const processCommand = (term: Terminal, command: string) => {
    const trimmed = command.trim();
    
    if (!trimmed) {
      (term as any).showPrompt();
      return;
    }

    const parts = trimmed.split(' ');
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch (cmd) {
      case 'help':
        term.writeln('\x1b[32mAvailable commands:\x1b[0m');
        term.writeln('  help     - Show this help message');
        term.writeln('  ls       - List files');
        term.writeln('  pwd      - Print working directory');
        term.writeln('  echo     - Print text');
        term.writeln('  clear    - Clear terminal');
        term.writeln('  date     - Show current date/time');
        term.writeln('  whoami   - Show current user');
        term.writeln('  cat      - Display file content');
        term.writeln('  mkdir    - Create directory');
        term.writeln('  touch    - Create file');
        term.writeln('  cd       - Change directory');
        break;

      case 'ls':
        term.writeln('\x1b[34mdocuments\x1b[0m  \x1b[34msrc\x1b[0m  \x1b[34mpublic\x1b[0m  \x1b[32mREADME.md\x1b[0m  \x1b[32mpackage.json\x1b[0m');
        break;

      case 'pwd':
        term.writeln('/home/user/project');
        break;

      case 'echo':
        term.writeln(args.join(' '));
        break;

      case 'clear':
        term.clear();
        break;

      case 'date':
        term.writeln(new Date().toString());
        break;

      case 'whoami':
        term.writeln('user');
        break;

      case 'cat':
        if (args.length === 0) {
          term.writeln('\x1b[31mcat: missing file operand\x1b[0m');
        } else {
          term.writeln(`\x1b[32mContent of ${args[0]}:\x1b[0m`);
          term.writeln('This is a simulated file content.');
        }
        break;

      case 'mkdir':
        if (args.length === 0) {
          term.writeln('\x1b[31mmkdir: missing operand\x1b[0m');
        } else {
          term.writeln(`\x1b[32mCreated directory: ${args[0]}\x1b[0m`);
        }
        break;

      case 'touch':
        if (args.length === 0) {
          term.writeln('\x1b[31mtouch: missing file operand\x1b[0m');
        } else {
          term.writeln(`\x1b[32mCreated file: ${args[0]}\x1b[0m`);
        }
        break;

      case 'cd':
        if (args.length === 0 || args[0] === '~') {
          term.writeln('');
        } else {
          term.writeln(`\x1b[32mChanged to directory: ${args[0]}\x1b[0m`);
        }
        break;

      default:
        term.writeln(`\x1b[31mCommand not found: ${cmd}\x1b[0m`);
        term.writeln('\x1b[90mType "help" for available commands\x1b[0m');
    }

    (term as any).showPrompt();
  };

  // Fit terminal when opened or maximized state changes
  useEffect(() => {
    if (isOpen && fitAddon.current) {
      setTimeout(() => fitAddon.current?.fit(), 100);
    }
  }, [isOpen, isMaximized]);

  if (!isOpen) return null;

  return (
    <div 
      className={`bg-gray-900 border-t border-gray-700 flex flex-col ${
        isMaximized ? 'h-96' : 'h-48'
      }`}
    >
      {/* Terminal Header */}
      <div className="flex items-center justify-between px-3 py-1 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">TERMINAL</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMaximized(!isMaximized)}
            className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
            title={isMaximized ? 'Minimize' : 'Maximize'}
          >
            {isMaximized ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
            title="Close"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Terminal Body */}
      <div 
        ref={terminalRef} 
        className="flex-1 overflow-hidden"
        style={{ backgroundColor: '#1e1e1e' }}
      />
    </div>
  );
}